/**
 * Trace editor — draw + edit a single closed Bézier shape per layer.
 *
 * DRAW: tap to place nodes. A tap near a symmetry-equivalent copy of an
 * existing node (on *any* visible Trace layer) snaps to it and links the new
 * node to its source, so the two move in lockstep forever (brief §4–5). Tapping
 * the first node closes the shape.
 *
 * On close the shape auto-enters EDIT mode: every root node is classified and
 * given handles (smooth-sym / smooth-asym / cusp / one-sided), and the layer is
 * a draft until the user commits (OK) or discards (Cancel).
 *
 * EDIT has two sub-modes:
 *   • handles — drag Bézier handles; tap a node to select it (→ Flip FAB)
 *   • points  — drag nodes to reposition them
 * Every edit is pushed through the node's link to its root and re-derived onto
 * all linked copies — the app's killer feature.
 */
import { useWrapTransform } from '@/composables/useWrapTransform';
import { compose, transformToMatrix, type Mat } from '@/geometry/affine';
import {
  autoClassify,
  cellCornerAnchors,
  constrainSelfFixedHandles,
  deriveHandles,
  edgeLinkCorrespondence,
  findSelfFixingOp,
  flipHandleType,
  tracePathsFrom,
  type TracePath,
} from '@/geometry/trace';
import { useCanvasStore } from '@/stores/canvas';
import { MAX_TRACE_LAYERS, useLayersStore } from '@/stores/layers';
import { usePatternStore } from '@/stores/pattern';
import { useToolsStore } from '@/stores/tools';
import { useUiStore } from '@/stores/ui';
import type { TraceLayerData, TraceNode } from '@/types/drawing';
import { computed, ref } from 'vue';
import { applyLinear, computeSnap, nearestSnapCandidate, SNAP_PX, snapCandidates, type AnchorLike, type SnapHit } from './inkSnap';

/** Opaque, narrow "architect's ink trace" stroke — contrasts with the bg (§rendering). */
const TRACE_STROKE: Record<'dark' | 'light', string> = {
  dark: '#f5f1ff',
  light: '#141019',
};
const TRACE_STROKE_WIDTH = 0.012; // tile units — fineliner

/** Completed-trace hue palette (08a §5): a rotating set assigned in creation
 *  order, so adjacent regions read as distinct colours. Stored on the layer at
 *  commit (stable across sessions). */
const TRACE_HUES = [265, 12, 150, 200, 45, 320, 95, 230];
function completedTraceStyle(hue: number): { stroke: string; fill: string } {
  return {
    stroke: `hsl(${hue}, 60%, 42%)`, // darkish outline
    fill: `hsla(${hue}, 65%, 60%, 0.28)`, // faint, lighter fill of the same hue
  };
}
const CLOSE_RADIUS = 0.035; // tile units
/** screen-space hit radius for nodes/handles in edit mode (finger-sized) */
const HIT_PX = 18;
/** close-time link validation: snapped nodes sit ~exactly on a transformed copy */
const LINK_POS_TOL = 0.012; // tile units — source→node position match
/** per-edge correspondence tolerance for a genuine seam (vs a coincident point).
 *  NOT half of the old summed 0.06 whole-node tolerance: that test let one
 *  already-working edge run to ~0.05 while its partner was near-zero, so a naive
 *  half would regress currently-linking seams. Flagged for on-device tuning. */
const LINK_EDGE_TOL = 0.045; // tile units — per-handle edge match

export type TraceMode = 'draw' | 'edit';
export type TraceSubMode = 'handles' | 'points';

function nodeId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createTraceEditor() {
  const layers = useLayersStore();
  const patternStore = usePatternStore();
  const canvasStore = useCanvasStore();
  const tools = useToolsStore();
  const ui = useUiStore();
  const wrap = useWrapTransform();

  const mode = ref<TraceMode>('draw');
  const subMode = ref<TraceSubMode>('points');
  const selectedNodeId = ref<string | null>(null);
  /** live cursor for the rubber-band preview */
  const cursor = ref<[number, number] | null>(null);
  const snapPreview = ref<SnapHit | null>(null);
  /** non-intrusive "tap to move the points" hint, dismissed on first interaction */
  const showEditToast = ref(false);
  const tick = ref(0);

  /** which control point is being dragged in handle sub-mode */
  let dragHandle: { nodeId: string; which: 'in' | 'out' } | null = null;
  let dragNodeId: string | null = null;
  /** The "active" node (brief 08 §4, model replaced by 08a): the last-placed
   *  node IFF it was placed by a snap (onto a real or ghost lattice node).
   *  Only an active node projects live drawn-edge-graph walks you can tap
   *  along (pathPreviews); a free tap clears it. */
  const activeNodeId = ref<string | null>(null);
  /** Draw-mode placement history — one entry per tap, listing the node id(s)
   *  that tap added (N>1 for a snap-along-line multi-place, §4). Drives
   *  whole-segment-per-tap step undo (§7) and the pinch rollback (§6). */
  let placeHistory: string[][] = [];

  const canFlip = computed(() => {
    if (mode.value !== 'edit' || subMode.value !== 'handles' || selectedNodeId.value === null) return false;
    // Flip is a from-scratch, whole-node type derivation; a linked/corner node
    // has no single independent type to flip (its shape follows its seam
    // partner(s)), so Flip is only offered on a fully-unlinked node.
    const sel = layers.editingTraceLayer?.nodes.find((n) => n.id === selectedNodeId.value);
    return !!sel && !sel.inLink && !sel.outLink;
  });

  function tileRadius(px: number): number {
    const scale = canvasStore.viewTransform.k * patternStore.tileSize;
    return scale > 0 ? px / scale : 0.02;
  }

  // ---- snap targets: nodes on every visible Trace layer (global, §2) plus the
  // unit-cell corners / grid crossings (§4). Corners are anchors with no node
  // identity — they contribute a snap POSITION only (no link/handle inheritance).
  function snapTargets(): AnchorLike[] {
    const out: AnchorLike[] = [];
    for (const layer of layers.traceLayers) {
      if (!layer.visible) continue;
      for (const n of layer.nodes) {
        out.push({ x: n.x, y: n.y, inX: n.inX, inY: n.inY, outX: n.outX, outY: n.outY, layerId: layer.id, nodeId: n.id });
      }
    }
    for (const c of cellCornerAnchors(patternStore.currentPattern.polygon())) {
      out.push({ x: c.x, y: c.y, inX: 0, inY: 0, outX: 0, outY: 0 });
    }
    return out;
  }

  /** Ring cap for how far active-path lines and always-on markers reach: the
   *  visible viewport (so they fill the screen without running off the plane),
   *  bounded for per-frame cost. Reuses useWrapTransform's viewport ring count. */
  function viewportSteps(): number {
    return Math.min(10, Math.max(2, wrap.visibleRings.value + 1));
  }

  /** The one shared candidate set — nodes + corners, expanded through the
   *  group orbit within the viewport's ring count — that markers, hover
   *  preview, and placement snapping all draw from, so they can never
   *  disagree about what's actually a snap target (bug: they used to be
   *  computed separately, with different ring counts). */
  function candidatesInView(): SnapHit[] {
    return snapCandidates(patternStore.currentVariant, snapTargets(), viewportSteps());
  }

  /** Every available snap target within the viewport, as tile-space points,
   *  for the always-on '+' markers (§1/§2) — deduped by position purely for
   *  drawing (no point stacking two identical '+'s); the underlying
   *  candidates themselves are NOT deduped this way (see snapCandidates).
   *  Draw-mode only. */
  function snapMarkers(): [number, number][] {
    if (mode.value !== 'draw') return [];
    const seen = new Set<string>();
    const out: [number, number][] = [];
    for (const c of candidatesInView()) {
      const key = `${Math.round(c.x * 1000)},${Math.round(c.y * 1000)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push([c.x, c.y]);
    }
    return out;
  }

  /**
   * Resolve one handle slot of a (layer, node) to the *root* handle driving it,
   * plus the transform mapping that root handle → this node's slot. Follows the
   * matching per-handle link one hop if present (keeping handle links single-hop,
   * exactly as the old whole-node `resolveRoot` did); otherwise the node's own
   * slot is the root, with `matrix: null`.
   */
  function resolveHandleRoot(
    layerId: string,
    nId: string,
    slot: 'in' | 'out',
  ): { sourceLayerId: string; sourceNodeId: string; sourceSlot: 'in' | 'out'; matrix: Mat | null } {
    const node = layers.findTraceLayer(layerId)?.nodes.find((n) => n.id === nId);
    const link = slot === 'in' ? node?.inLink : node?.outLink;
    if (link) {
      return {
        sourceLayerId: link.sourceLayerId,
        sourceNodeId: link.sourceNodeId,
        sourceSlot: link.sourceSlot,
        matrix: link.matrix,
      };
    }
    return { sourceLayerId: layerId, sourceNodeId: nId, sourceSlot: slot, matrix: null };
  }

  // ---- DRAW ----

  function ensureDraft(): TraceLayerData | null {
    const existing = layers.editingTraceLayer;
    if (existing) return existing;
    if (layers.traceLayers.length >= MAX_TRACE_LAYERS) return null;
    const layer: TraceLayerData = {
      id: nodeId(),
      name: layers.nextTraceName(),
      opacity: 1,
      visible: true,
      nodes: [],
      closed: false,
      d: '',
      stroke: TRACE_STROKE[ui.theme],
      strokeWidth: TRACE_STROKE_WIDTH,
      drawMode: 'outline',
      timestamp: Date.now(),
    };
    layers.startTraceLayer(layer);
    return layer;
  }

  function placeNode(wx: number, wy: number): void {
    const layer = ensureDraft();
    if (!layer) return;
    // Snap the *position* to a nearby valid transformed-point (accuracy only);
    // the link relationship is decided at close, with the whole shape in view.
    const snap = nearestSnapCandidate(candidatesInView(), wx, wy, tileRadius(SNAP_PX));
    const x = snap ? snap.x : wx;
    const y = snap ? snap.y : wy;
    const id = nodeId();
    layer.nodes.push({ id, x, y, inX: 0, inY: 0, outX: 0, outY: 0, type: 'cusp' });
    placeHistory.push([id]);
    // §4: only a snapped placement proves the node sits on the lattice → active
    // (shows drawn-edge-graph walks, 08a); a free tap clears the active node.
    activeNodeId.value = snap ? id : null;
    layers.rebuildTraceD(layer);
    snapPreview.value = null;
  }

  /** pathPreviews() memo cache — computing the graph is O(edges × transforms)
   *  and the overlay calls this every frame; recompute only when the inputs
   *  that can change the answer actually change. */
  let pathPreviewsCacheKey = '';
  let pathPreviewsCache: TracePath[] = [];

  /** The active node's live drawn-edge-graph walks, for the overlay (brief 08a). */
  function pathPreviews(): TracePath[] {
    const layer = layers.editingTraceLayer;
    const active = layer?.nodes.find((n) => n.id === activeNodeId.value);
    if (mode.value !== 'draw' || !layer || !active) return [];

    const key = `${activeNodeId.value}|${layer.nodes.length}|${tick.value}|${layers.revision}`;
    if (key === pathPreviewsCacheKey) return pathPreviewsCache;

    const traces = layers.traceLayers
      .filter((l) => l.visible)
      .map((l) => ({ nodes: l.nodes.map((n) => ({ x: n.x, y: n.y })), closed: l.closed }));
    const transforms = patternStore.currentVariant.transformsInRange(viewportSteps()).map(transformToMatrix);
    const tol = tileRadius(SNAP_PX);
    const activeIdx = layer.nodes.findIndex((n) => n.id === activeNodeId.value);
    const prevNode = activeIdx > 0 ? layer.nodes[activeIdx - 1] : undefined;
    const exclude = prevNode ? [{ x: prevNode.x, y: prevNode.y }] : [];

    pathPreviewsCacheKey = key;
    pathPreviewsCache = tracePathsFrom(active, traces, transforms, tol, exclude);
    return pathPreviewsCache;
  }

  /**
   * Brief 08a: if there's an active node and this tap lands on a point along
   * one of its drawn-edge-graph walks, place EVERY intermediate vertex from
   * the active node up to the tapped point in a single action (one undo step,
   * §7). The furthest placed vertex becomes the new active node (reached by a
   * snap). If the walk reaches the start node, close the shape instead.
   * Returns true if it handled the tap.
   */
  function snapAlongActivePath(wx: number, wy: number): boolean {
    const layer = layers.editingTraceLayer;
    const active = layer?.nodes.find((n) => n.id === activeNodeId.value);
    if (mode.value !== 'draw' || !layer || !active) return false;
    const tol = tileRadius(SNAP_PX * 1.5); // a touch more generous for path taps

    let best: { path: TracePath; idx: number; dist: number } | null = null;
    for (const path of pathPreviews()) {
      for (let idx = 0; idx < path.points.length; idx++) {
        const [px, py] = path.points[idx]!;
        const d = Math.hypot(px - wx, py - wy);
        if (d <= tol && (!best || d < best.dist)) best = { path, idx, dist: d };
      }
    }
    if (!best) return false;

    const points = best.path.points.slice(0, best.idx + 1);
    const start = layer.nodes[0];
    const [lastX, lastY] = points[points.length - 1]!;
    const reachesStart =
      !!start && layer.nodes.length > 2 && Math.hypot(lastX - start.x, lastY - start.y) < CLOSE_RADIUS;
    // Place intermediates; when the target is the existing start node, stop one
    // short (don't duplicate it) and close instead.
    const toPlace = reachesStart ? points.slice(0, -1) : points;
    const added: string[] = [];
    for (const [x, y] of toPlace) {
      // Guard: skip a point that lands on the active node itself (shouldn't
      // happen given walks start away from active, but don't duplicate it).
      if (Math.hypot(x - active.x, y - active.y) <= tol) continue;
      const id = nodeId();
      layer.nodes.push({ id, x, y, inX: 0, inY: 0, outX: 0, outY: 0, type: 'cusp' });
      added.push(id);
    }
    if (added.length) placeHistory.push(added);
    layers.rebuildTraceD(layer);
    snapPreview.value = null;
    if (reachesStart) {
      close();
    } else {
      activeNodeId.value = added[added.length - 1] ?? activeNodeId.value;
    }
    return true;
  }

  /** Remove the vertices added by the most recent tap (whole-segment-per-tap
   *  step undo, §7; also the §6 pinch rollback). Draw mode only; recomputes the
   *  active node from whatever the previous tap left. */
  function popStep(): void {
    if (mode.value !== 'draw' || placeHistory.length === 0) return;
    const layer = layers.editingTraceLayer;
    if (!layer) {
      placeHistory = [];
      return;
    }
    const step = placeHistory.pop()!;
    const ids = new Set(step);
    layer.nodes = layer.nodes.filter((n) => !ids.has(n.id));
    layers.rebuildTraceD(layer);
    // The node now at the tail is active iff the previous tap that placed it was
    // itself a snap. We don't retain per-node snap flags across undo, so
    // conservatively clear active — the next tap re-establishes it on a snap.
    activeNodeId.value = null;
    snapPreview.value = null;
    tick.value++;
  }

  /** Step undo affordance (§7) — one whole tap's worth of vertices. */
  function undoStep(): void {
    popStep();
  }

  /** §6: a pinch's first finger already placed a node; roll that tap back when
   *  the second finger reveals it as a zoom gesture. */
  function cancelLastPlacement(): void {
    popStep();
  }

  /** Whether a draft placement can be stepped back (drives the Undo FAB). */
  const canUndoStep = computed(() => mode.value === 'draw' && placeHistory.length > 0);

  /**
   * Decide link relationships for the whole (closed) shape — deferred from
   * placement so the full point set is known (brief: validate against the real
   * group operations, not proximity). A node links to an earlier same-layer node
   * or a node on another visible layer only when a genuine group operation maps
   * that source onto it AND maps the source's neighbours onto the node's
   * neighbours (a real seam), not merely because the points coincide.
   */
  function resolveLinks(layer: TraceLayerData): void {
    const variant = patternStore.currentVariant;
    const nodes = layer.nodes;
    const n = nodes.length;
    for (const nd of nodes) {
      nd.inLink = undefined; // re-decide from scratch each close
      nd.outLink = undefined;
    }
    if (n < 3) return;

    /** one per-handle candidate: link N's `targetSlot` handle to a source slot. */
    type Cand = {
      targetSlot: 'in' | 'out';
      sourceLayerId: string;
      sourceNodeId: string;
      sourceSlot: 'in' | 'out';
      matrix: Mat;
      err: number;
    };

    // Emit up to two candidates for one source S: one per edge of N that
    // corresponds to a (possibly different) slot of S. A corner links each edge
    // to a different source under a different op — hence per-edge, not summed.
    const consider = (
      N: TraceNode,
      nPrev: TraceNode,
      nNext: TraceNode,
      S: TraceNode,
      sNodes: readonly TraceNode[],
      sLayerId: string,
    ): Cand[] => {
      if (S.id === N.id) return [];
      const sn = sNodes.length;
      if (sn < 3) return [];
      const si = sNodes.indexOf(S);
      if (si < 0) return [];
      // is N a genuine transformed copy of S's position? (single-source, tight tol)
      const hit = computeSnap(variant, [{ x: S.x, y: S.y, inX: 0, inY: 0, outX: 0, outY: 0 }], N.x, N.y, LINK_POS_TOL);
      if (!hit) return [];
      const sPrev = sNodes[(si - 1 + sn) % sn]!;
      const sNext = sNodes[(si + 1) % sn]!;
      const corr = edgeLinkCorrespondence(hit.matrix, sPrev, sNext, nPrev, nNext);
      const out: Cand[] = [];
      const build = (targetSlot: 'in' | 'out', winner: { slot: 'in' | 'out'; err: number }): void => {
        if (winner.err >= LINK_EDGE_TOL) return; // coincident edge, not a real seam
        // resolve S's winning slot to its root handle so the link is single-hop
        const root = resolveHandleRoot(sLayerId, S.id, winner.slot);
        if (root.sourceLayerId === layer.id && root.sourceNodeId === N.id) return; // self/cycle
        const matrix = root.matrix ? compose(hit.matrix, root.matrix) : hit.matrix;
        out.push({
          targetSlot,
          sourceLayerId: root.sourceLayerId,
          sourceNodeId: root.sourceNodeId,
          sourceSlot: root.sourceSlot,
          matrix,
          err: winner.err,
        });
      };
      build('in', corr.in);
      build('out', corr.out);
      return out;
    };

    for (let i = 0; i < n; i++) {
      const N = nodes[i]!;
      const nPrev = nodes[(i - 1 + n) % n]!;
      const nNext = nodes[(i + 1) % n]!;
      const inCands: Cand[] = [];
      const outCands: Cand[] = [];
      const collect = (cands: Cand[]): void => {
        for (const c of cands) (c.targetSlot === 'in' ? inCands : outCands).push(c);
      };
      for (let j = 0; j < i; j++) collect(consider(N, nPrev, nNext, nodes[j]!, nodes, layer.id)); // earlier, same layer
      for (const other of layers.traceLayers) {
        if (other.id === layer.id || !other.visible) continue;
        for (const S of other.nodes) collect(consider(N, nPrev, nNext, S, other.nodes, other.id));
      }
      const lowest = (cands: Cand[]): Cand | null => cands.reduce<Cand | null>((b, c) => (!b || c.err < b.err ? c : b), null);
      const bestIn = lowest(inCands);
      const bestOut = lowest(outCands);
      if (bestIn) {
        N.inLink = {
          sourceLayerId: bestIn.sourceLayerId,
          sourceNodeId: bestIn.sourceNodeId,
          sourceSlot: bestIn.sourceSlot,
          matrix: bestIn.matrix,
        };
      }
      if (bestOut) {
        N.outLink = {
          sourceLayerId: bestOut.sourceLayerId,
          sourceNodeId: bestOut.sourceNodeId,
          sourceSlot: bestOut.sourceSlot,
          matrix: bestOut.matrix,
        };
      }
    }
  }

  function close(): void {
    const layer = layers.editingTraceLayer;
    if (!layer || layer.nodes.length < 3) {
      cancel();
      return;
    }
    layer.closed = true;
    resolveLinks(layer); // decide genuine symmetry links with the full shape known
    layer.nodes = autoClassify(layer.nodes, true, patternStore.currentVariant);
    // Derive each linked handle from its (now fully-classified root) source, one
    // forward pass. Sources are always roots, so this is order-independent; a
    // one-link node's free handle keeps its autoClassify default.
    for (const nd of layer.nodes) {
      if (nd.inLink) {
        const src = layers.findTraceLayer(nd.inLink.sourceLayerId)?.nodes.find((s) => s.id === nd.inLink!.sourceNodeId);
        if (src) {
          const hx = nd.inLink.sourceSlot === 'in' ? src.inX : src.outX;
          const hy = nd.inLink.sourceSlot === 'in' ? src.inY : src.outY;
          [nd.inX, nd.inY] = applyLinear(nd.inLink.matrix, hx, hy);
        }
      }
      if (nd.outLink) {
        const src = layers.findTraceLayer(nd.outLink.sourceLayerId)?.nodes.find((s) => s.id === nd.outLink!.sourceNodeId);
        if (src) {
          const hx = nd.outLink.sourceSlot === 'in' ? src.inX : src.outX;
          const hy = nd.outLink.sourceSlot === 'in' ? src.inY : src.outY;
          [nd.outX, nd.outY] = applyLinear(nd.outLink.matrix, hx, hy);
        }
      }
    }
    layers.rebuildTraceD(layer);
    mode.value = 'edit';
    subMode.value = 'points';
    selectedNodeId.value = null;
    activeNodeId.value = null;
    placeHistory = [];
    showEditToast.value = true;
    tick.value++;
  }

  // ---- EDIT: hit-testing ----

  function hitNode(wx: number, wy: number): TraceNode | null {
    const layer = layers.editingTraceLayer;
    if (!layer) return null;
    const r = tileRadius(HIT_PX);
    let best: TraceNode | null = null;
    let bestD = r;
    for (const n of layer.nodes) {
      const d = Math.hypot(n.x - wx, n.y - wy);
      if (d < bestD) {
        best = n;
        bestD = d;
      }
    }
    return best;
  }

  function hitHandle(wx: number, wy: number): { node: TraceNode; which: 'in' | 'out' } | null {
    const layer = layers.editingTraceLayer;
    if (!layer) return null;
    const r = tileRadius(HIT_PX);
    let best: { node: TraceNode; which: 'in' | 'out' } | null = null;
    let bestD = r;
    for (const n of layer.nodes) {
      const dOut = Math.hypot(n.x + n.outX - wx, n.y + n.outY - wy);
      if ((n.outX !== 0 || n.outY !== 0) && dOut < bestD) {
        best = { node: n, which: 'out' };
        bestD = dOut;
      }
      const dIn = Math.hypot(n.x + n.inX - wx, n.y + n.inY - wy);
      if ((n.inX !== 0 || n.inY !== 0) && dIn < bestD) {
        best = { node: n, which: 'in' };
        bestD = dIn;
      }
    }
    return best;
  }

  // ---- gesture entry points (called by TileCanvas) ----

  function onDown(wx: number, wy: number): void {
    showEditToast.value = false;
    // The layer we were editing can be deleted from the layer panel (or cleared
    // by a new/loaded sketch) while we still think we're in edit mode — with no
    // editing layer the FABs are hidden, so recover to draw here or a new trace
    // could never be started.
    if (mode.value === 'edit' && !layers.editingTraceLayer) {
      mode.value = 'draw';
      subMode.value = 'points';
      selectedNodeId.value = null;
    }
    if (mode.value === 'draw') {
      const layer = layers.editingTraceLayer;
      // Brief 08a: if the previous node is "active" (placed by a snap) and this
      // tap lands on one of its drawn-edge-graph walks, place every
      // intermediate vertex up to it in one action (closing via the walk's own
      // logic if it reaches the start) — tried FIRST, before the plain
      // tap-near-start check below, so a tap near the closing point of a
      // highlighted path still fills the intermediate nodes rather than
      // short-circuiting straight to close().
      if (snapAlongActivePath(wx, wy)) {
        tick.value++;
        return;
      }
      const first = layer?.nodes[0];
      if (first && layer!.nodes.length > 2 && Math.hypot(wx - first.x, wy - first.y) < CLOSE_RADIUS) {
        close();
        return;
      }
      placeNode(wx, wy);
      tick.value++;
      return;
    }

    // edit mode
    if (subMode.value === 'handles') {
      const h = hitHandle(wx, wy);
      if (h) {
        dragHandle = { nodeId: h.node.id, which: h.which };
        selectedNodeId.value = null; // a handle drag takes priority over selection
        tick.value++;
        return;
      }
      const n = hitNode(wx, wy);
      selectedNodeId.value = n ? n.id : null; // tap node = select; tap empty = deselect
    } else {
      const n = hitNode(wx, wy);
      dragNodeId = n ? n.id : null;
    }
    tick.value++;
  }

  function onMove(wx: number, wy: number): void {
    cursor.value = [wx, wy];
    if (mode.value === 'draw') {
      snapPreview.value = nearestSnapCandidate(candidatesInView(), wx, wy, tileRadius(SNAP_PX));
      tick.value++;
      return;
    }
    const layer = layers.editingTraceLayer;
    if (!layer) return;
    if (dragHandle) {
      const node = layer.nodes.find((n) => n.id === dragHandle!.nodeId);
      if (node) dragHandleTo(layer.id, node, dragHandle.which, wx, wy);
    } else if (dragNodeId) {
      layers.applyNodeEdit(layer.id, dragNodeId, { x: wx, y: wy });
    }
    tick.value++;
  }

  function onUp(): void {
    dragHandle = null;
    dragNodeId = null;
    tick.value++;
  }

  /** Clear the hover/snap preview when the pointer leaves the canvas entirely
   *  (mouse moving to a toolbar, an Apple Pencil lifted out of hover range,
   *  etc.) — otherwise the rubber-band line and the highlighted snap target
   *  stay stuck at wherever the pointer last was, rather than disappearing
   *  once it's no longer actually there. Distinct from reset(): doesn't
   *  touch the draft, mode, or selection, so it's safe to call whenever the
   *  pointer leaves, not just when abandoning the whole draft. */
  function onHoverEnd(): void {
    cursor.value = null;
    snapPreview.value = null;
    tick.value++;
  }

  /** Compute coupled in/out for a node given the dragged control, then commit. */
  function dragHandleTo(layerId: string, node: TraceNode, which: 'in' | 'out', wx: number, wy: number): void {
    const hx = wx - node.x;
    const hy = wy - node.y;
    let inX = node.inX;
    let inY = node.inY;
    let outX = node.outX;
    let outY = node.outY;
    const len = Math.hypot(hx, hy) || 1e-9;

    // A node with even one per-handle link is driven per-slot: send ONLY the
    // dragged handle's field(s). Bundling both (as the coupling paths below do)
    // would push the untouched handle's value through its own — possibly
    // unrelated — link and trigger a spurious propagation across that group. The
    // self-fix / type coupling below is a from-scratch derivation valid only for
    // a fully-independent node; a linked node is already constrained by its link.
    if (node.inLink || node.outLink) {
      if (which === 'out') layers.applyNodeEdit(layerId, node.id, { outX: hx, outY: hy });
      else layers.applyNodeEdit(layerId, node.id, { inX: hx, inY: hy });
      return;
    }

    // Bug 1 (shared-edge constraint): a node at a self-fixed shared-edge point
    // (see findSelfFixingOp) can never become an unconstrained curve — drive
    // the other handle through the fixing op's linear part so the pair is
    // valid by construction, the same way a linked node's copy is driven by
    // its root, instead of falling through to the ordinary per-type coupling
    // below (which knows nothing about the tiling and would let the user drag
    // this into an invalid curve). Only unlinked nodes are candidates — a
    // linked node is already driven by (and constrained through) its link.
    if (!node.inLink && !node.outLink) {
      const layer = layers.findTraceLayer(layerId);
      const n = layer?.nodes.length ?? 0;
      if (layer && n >= 3) {
        const idx = layer.nodes.indexOf(node);
        const prev = layer.nodes[(idx - 1 + n) % n]!;
        const next = layer.nodes[(idx + 1) % n]!;
        const op = findSelfFixingOp(patternStore.currentVariant, node, prev, next);
        if (op) {
          const [tx, ty] = applyLinear(op.matrix, hx, hy);
          if (op.swap) {
            // common case: the op glues this edge to itself at its midpoint —
            // the dragged handle's image under the op becomes the other handle.
            if (which === 'out') {
              outX = hx; outY = hy; inX = tx; inY = ty;
            } else {
              inX = hx; inY = hy; outX = tx; outY = ty;
            }
          } else {
            // rare: the fixing op's axis runs along the path itself through
            // this node — each handle must be invariant on its own. Project
            // the dragged vector onto that invariant direction (exact for an
            // involution: a vector averaged with its own image under the op
            // lies on the op's fixed axis).
            const px = (hx + tx) / 2;
            const py = (hy + ty) / 2;
            if (which === 'out') { outX = px; outY = py; } else { inX = px; inY = py; }
          }
          layers.applyNodeEdit(layerId, node.id, { inX, inY, outX, outY });
          return;
        }
      }
    }

    if (node.type === 'smooth-sym') {
      if (which === 'out') {
        outX = hx; outY = hy; inX = -hx; inY = -hy;
      } else {
        inX = hx; inY = hy; outX = -hx; outY = -hy;
      }
    } else if (node.type === 'smooth-asym') {
      // keep both collinear; preserve the opposite handle's length
      if (which === 'out') {
        outX = hx; outY = hy;
        const il = Math.hypot(inX, inY);
        inX = (-hx / len) * il; inY = (-hy / len) * il;
      } else {
        inX = hx; inY = hy;
        const ol = Math.hypot(outX, outY);
        outX = (-hx / len) * ol; outY = (-hy / len) * ol;
      }
    } else {
      // cusp / one-sided — handles are independent
      if (which === 'out') { outX = hx; outY = hy; } else { inX = hx; inY = hy; }
    }
    layers.applyNodeEdit(layerId, node.id, { inX, inY, outX, outY });
  }

  // ---- FAB actions ----

  function toggleSubMode(): void {
    showEditToast.value = false;
    subMode.value = subMode.value === 'handles' ? 'points' : 'handles';
    selectedNodeId.value = null;
    tick.value++;
  }

  function flipSelected(): void {
    const sel = selectedNodeId.value;
    const layer = layers.editingTraceLayer;
    if (!sel || !layer) return;
    const node = layer.nodes.find((n) => n.id === sel);
    // canFlip already gates this to a fully-unlinked node; bail defensively if
    // that ever slips (a linked node has no independent type to flip).
    if (!node || node.inLink || node.outLink) return;
    const n = layer.nodes.length;
    const idx = layer.nodes.indexOf(node);
    const prev = layer.nodes[(idx - 1 + n) % n]!;
    const next = layer.nodes[(idx + 1) % n]!;
    const newType = flipHandleType(node.type);
    let h = deriveHandles(prev, node, next, newType);
    // Bug 1: Flip recomputes handles from scratch (like autoClassify), so it
    // needs the same shared-edge constraint check — otherwise flipping a
    // self-fixed node's type is a second way to sneak past it.
    const op = findSelfFixingOp(patternStore.currentVariant, node, prev, next);
    if (op) h = constrainSelfFixedHandles(h, op);
    layers.applyNodeEdit(layer.id, node.id, { type: newType, ...h });
    tick.value++;
  }

  /** OK FAB — commit the draft to the stack. */
  function commit(): void {
    // §5: give the finished trace its own hue (dark outline + faint fill), in
    // creation order, stored on the layer so it's stable across sessions.
    const layer = layers.editingTraceLayer;
    if (layer && !layer.fill) {
      const used = layers.traceLayers.filter((l) => l.id !== layer.id && l.fill).length;
      const { stroke, fill } = completedTraceStyle(TRACE_HUES[used % TRACE_HUES.length]!);
      layer.stroke = stroke;
      layer.fill = fill;
    }
    layers.commitTraceLayer();
    reset();
    // Brief addendum §5: Trace is a one-shot action per shape — once it
    // completes one, hand off to the sketch/draw tool rather than leaving
    // Trace selected, mirroring exactly what ToolStrip's Draw button does.
    layers.setMode('sketch');
    tools.activeTool = 'pen';
  }

  /** Cancel FAB — discard the draft. */
  function cancel(): void {
    layers.cancelTraceLayer();
    reset();
  }

  /** Return to a clean draw state (also called on leaving Trace mode). */
  function reset(): void {
    if (layers.editingTraceLayerId) layers.cancelTraceLayer();
    mode.value = 'draw';
    subMode.value = 'points';
    selectedNodeId.value = null;
    cursor.value = null;
    snapPreview.value = null;
    showEditToast.value = false;
    dragHandle = null;
    dragNodeId = null;
    activeNodeId.value = null;
    placeHistory = [];
    tick.value++;
  }

  /**
   * Layer panel's "Edit" button: resume editing an already-committed trace
   * layer — parallel to `close()` auto-entering edit mode, but for a layer
   * that's already closed/classified (no re-close/re-classify needed, and
   * definitely not `cancelTraceLayer`, which would delete it). Refuses if a
   * DIFFERENT layer is currently a live draft/edit, so it can't be silently
   * clobbered — the layer panel only offers the button when nothing else is
   * being edited.
   */
  function editLayer(id: string): void {
    if (layers.editingTraceLayerId && layers.editingTraceLayerId !== id) return;
    layers.beginEditingTraceLayer(id);
    mode.value = 'edit';
    subMode.value = 'points';
    selectedNodeId.value = null;
    activeNodeId.value = null;
    placeHistory = [];
    tick.value++;
  }

  /** Rubber-band preview d while drawing (open path through cursor). */
  function previewD(): string {
    const layer = layers.editingTraceLayer;
    if (mode.value !== 'draw' || !layer || layer.nodes.length === 0) return '';
    const a0 = layer.nodes[0]!;
    let d = `M ${a0.x.toFixed(4)} ${a0.y.toFixed(4)}`;
    for (let i = 1; i < layer.nodes.length; i++) {
      const c = layer.nodes[i]!;
      d += ` L ${c.x.toFixed(4)} ${c.y.toFixed(4)}`;
    }
    if (cursor.value) d += ` L ${cursor.value[0].toFixed(4)} ${cursor.value[1].toFixed(4)}`;
    return d;
  }

  return {
    mode,
    subMode,
    selectedNodeId,
    cursor,
    snapPreview,
    showEditToast,
    tick,
    canFlip,
    canUndoStep,
    activeNodeId,
    pathPreviews,
    snapMarkers,
    undoStep,
    onDown,
    onMove,
    onUp,
    onHoverEnd,
    cancelLastPlacement,
    close,
    toggleSubMode,
    flipSelected,
    commit,
    cancel,
    reset,
    editLayer,
    previewD,
  };
}

/**
 * Shared singleton — the canvas (input + overlay) and the FAB bar must drive the
 * same editor state, so the composable memoises one instance. Created lazily
 * inside an active Pinia context on first use.
 */
let instance: ReturnType<typeof createTraceEditor> | null = null;

export function useTraceEditor() {
  if (!instance) instance = createTraceEditor();
  return instance;
}
